import { Ambulance, ChevronRight } from 'lucide-react';
import { InlineNotice } from '@/components/medic/PatientCardStates';
import type { AmbulanceCallItem, AmbulanceHistory } from '@/components/medic/patientAmbulanceData';

// ============================================================================
// PatientAmbulanceHistoryBlock — «История вызовов» в карточке пациента.
// Данные — документ ambulance_calls (отбор по «Пациент»), представления
// адреса/статуса — серверные. Клик по вызову открывает форму документа.
// Пусто — явная подсказка «Вызовов не было».
// ============================================================================

interface Props {
    history: AmbulanceHistory;
    onOpenCall: (id: string) => void;
}

function statusTone(code: string): string {
    const value = String(code || '').toLowerCase();
    if (value === 'completed') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
    if (['en_route', 'on_scene'].includes(value)) return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400';
    if (['accepted', 'assigned'].includes(value)) return 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400';
    if (['cancelled', 'rejected'].includes(value)) return 'border-border bg-muted text-muted-foreground';
    return 'border-primary/30 bg-primary/10 text-primary';
}

function CallRow({ item, onOpen }: { item: AmbulanceCallItem; onOpen: () => void }) {
    const details = [
        item.address,
        item.crewCount === null ? '' : `бригада: ${item.crewCount} чел.`,
    ].filter(Boolean);

    return (
        <button
            type="button"
            onClick={onOpen}
            className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/50"
        >
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-medium">{item.calledAt || 'Время не указано'}</span>
                    {item.number && (
                        <span className="text-[11px] text-muted-foreground">№ {item.number}</span>
                    )}
                    {item.statusLabel && (
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] ${statusTone(item.statusCode)}`}>
                            {item.statusLabel}
                        </span>
                    )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                    {details.map((line) => <span key={line} className="break-words">{line}</span>)}
                </div>
                {item.outcome && (
                    <div className="mt-0.5 break-words text-xs">
                        <span className="text-muted-foreground">Итог: </span>
                        {item.outcome}
                    </div>
                )}
            </div>
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
    );
}

export function PatientAmbulanceHistoryBlock({ history, onOpenCall }: Props) {
    // В базе без вертикали «Скорая» блок не показываем (как профиль учётной записи).
    if (history.state === 'unavailable') return null;

    return (
        <section className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <Ambulance className="h-3.5 w-3.5 text-primary" />
                    История вызовов скорой
                </div>
                {history.state === 'ready' && history.items.length > 0 && (
                    <span className="rounded-full border bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                        {history.items.length}
                    </span>
                )}
            </div>

            {history.state !== 'ready' ? (
                <InlineNotice
                    text={history.error || history.note || 'История вызовов недоступна'}
                    tone={history.state === 'denied' || history.state === 'error' ? 'danger' : 'warning'}
                />
            ) : history.items.length === 0 ? (
                <div className="rounded-lg border border-dashed bg-card px-4 py-6 text-center">
                    <div className="text-sm font-medium">Вызовов не было</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                        Вызовы скорой, оформленные на этого пациента, появятся здесь и будут открываться по клику.
                    </div>
                </div>
            ) : (
                <div className="max-h-80 overflow-auto rounded-lg border bg-card">
                    {history.items.map((item, index) => (
                        <div key={item.id} className={index > 0 ? 'border-t' : ''}>
                            <CallRow item={item} onOpen={() => onOpenCall(item.id)} />
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
